// frontend/src/services/auth.service.ts

import apiService from "./api.service";
import {
  LoginFormData,
  RegisterFormData,
  AuthResponse,
  ApiResponse,
  ICompany,
  IStaffUser,
} from "@/types";

class AuthService {
  getGoogleAuthUrl(mode: "login" | "register"): string {
    const envUrl = import.meta.env.VITE_API_URL?.replace(/\/+$/, "");
    const baseURL = envUrl
      ? envUrl.endsWith("/api")
        ? `${envUrl}/v1`
        : envUrl
      : "http://localhost:5000/api/v1";

    return `${baseURL}/auth/google?mode=${mode}`;
  }

  /**
   * Register a new company
   */
  async register(data: RegisterFormData): Promise<ApiResponse> {
    return await apiService.post("/auth/register", data);
  }

  /**
   * Register a new staff account (OTP required)
   */
  async staffRegister(data: {
    companyId: string;
    name: string;
    email: string;
    contactNumber: string;
    role: string;
    password: string;
    confirmPassword: string;
  }): Promise<ApiResponse> {
    return await apiService.post("/auth/staff-register", data);
  }

  /**
   * Verify OTP
   */
  async verifyOTP(
    email: string,
    otp: string,
    isStaff?: boolean,
    companyId?: string
  ): Promise<ApiResponse<AuthResponse["data"]>> {
    return await apiService.post<AuthResponse["data"]>("/auth/verify-otp", {
      email,
      otp,
      ...(isStaff ? { isStaff: true, companyId } : {}),
    });
  }

  /**
   * Resend OTP
   */
  async resendOTP(
    email: string,
    isStaff?: boolean,
    companyId?: string,
  ): Promise<ApiResponse> {
    return await apiService.post("/auth/resend-otp", {
      email,
      isStaff,
      companyId,
    });
  }

  /**
   * Company login
   */
  async login(data: LoginFormData): Promise<ApiResponse<AuthResponse["data"]>> {
    const response = await apiService.post<AuthResponse["data"]>(
      "/auth/login",
      data,
    );

    if (response.success && response.data) {
      this.saveAuthData(response.data.token, response.data.refreshToken);
    }

    return response;
  }

  /**
   * Staff login
   */
  async staffLogin(
    email: string,
    password: string,
    companyId: string,
  ): Promise<ApiResponse<AuthResponse["data"]>> {
    const response = await apiService.post<AuthResponse["data"]>(
      "/auth/staff-login",
      {
        email,
        password,
        companyId,
      },
    );

    if (response.success && response.data) {
      this.saveAuthData(response.data.token, response.data.refreshToken);
    }

    return response;
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      await apiService.post("/auth/logout");
    } finally {
      this.clearAuthData();
    }
  }

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<
    ApiResponse<{
      token: string;
      refreshToken: string;
    }>
  > {
    const response = await apiService.post("/auth/refresh-token", {
      refreshToken,
    });

    if (response.success && response.data) {
      this.saveAuthData(response.data.token, response.data.refreshToken);
    }

    return response;
  }

  /**
   * Get current user from localStorage
   */
  getCurrentUser(): ICompany | IStaffUser | null {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  }

  /**
   * Get user type from localStorage
   */
  getUserType(): "company" | "staff" | null {
    return localStorage.getItem("userType") as "company" | "staff" | null;
  }

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem("token");
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  /**
   * Save authentication data to localStorage
   */
  private saveAuthData(token: string, refreshToken: string): void {
    localStorage.setItem("token", token);
    localStorage.setItem("refreshToken", refreshToken);
  }

  /**
   * Save user data to localStorage
   */
  saveUserData(user: ICompany | IStaffUser, type: "company" | "staff"): void {
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("userType", type);
  }

  /**
   * Clear all authentication data
   */
  private clearAuthData(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("userType");
  }
}

export default new AuthService();
